import {
  ComponentType,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useDeepCompareEffect from 'react-use/lib/useDeepCompareEffect';

import { useSearchParamsWithValues } from '../../hooks/useSearchParamsWithValues';
import { useLocalStorage } from '../../hooks/useStorage';
import { SearchUtils } from '../../utils';

export interface FilterEditorComponentProps<T> {
  filter: Filter<T>;
  value: T | null;
  onValueChange: (value: T) => void;
  onClose: () => void;
}

export interface FilterViewerComponentProps<T> {
  filter: Filter<T>;
  value: T;
  onValueChange: (value: T) => void;
  onRemove: () => void;
}

export interface FilterMeta {
  id: string;
  label: string;
  category?: string;
  hotkey?: string;
  placement?: 'inline' | 'overflow';
}

export interface EditorRenderProps {
  value: unknown;
  onValueChange: (value: unknown) => void;
  onClose: () => void;
}

export interface ViewerRenderProps {
  onValueChange: (value: unknown) => void;
  onRemove: () => void;
}

export interface FilterDefinition extends FilterMeta {
  activate(value: unknown): ActiveFilterEntry;
  renderEditor(props: EditorRenderProps): ReactNode;
}

export interface ActiveFilterEntry extends FilterMeta {
  value: unknown;
  activate(value: unknown): ActiveFilterEntry;
  buildQuery(): string;
  renderViewer(props: ViewerRenderProps): ReactNode;
  renderEditor(props: EditorRenderProps): ReactNode;
}

export interface Filter<T> extends FilterMeta {
  editor: ComponentType<FilterEditorComponentProps<T>>;
  viewer: ComponentType<FilterViewerComponentProps<T>>;
  query: (value: T) => string;
}

export function createFilter<T>(config: Filter<T>): FilterDefinition {
  const { editor: Editor, viewer, query, ...meta } = config;

  const renderEditor = ({
    value: editValue,
    onValueChange,
    onClose,
  }: EditorRenderProps) => (
    <Editor
      filter={config}
      value={(editValue ?? null) as T | null}
      onValueChange={onValueChange}
      onClose={onClose}
    />
  );

  const makeActive = (value: unknown): ActiveFilterEntry => ({
    ...meta,
    value,
    activate: (newValue: unknown) => makeActive(newValue),
    buildQuery: () => query(value as T),
    renderViewer: ({ onValueChange, onRemove }: ViewerRenderProps) => {
      const Viewer = viewer;
      return (
        <Viewer
          filter={config}
          value={value as T}
          onValueChange={onValueChange}
          onRemove={onRemove}
        />
      );
    },
    renderEditor,
  });

  return {
    ...meta,
    activate: (value: unknown) => makeActive(value),
    renderEditor,
  };
}

interface StoredFilter {
  id: string;
  value: unknown;
}

export interface SetFiltersEntry {
  filter: FilterDefinition | ActiveFilterEntry;
  value: unknown;
}

type FilterContextValue = {
  query: string;
  setQuery: (query: string) => void;
  filters: FilterDefinition[];
  activeFilters: ActiveFilterEntry[];
  addFilter: (
    filter: FilterDefinition | ActiveFilterEntry,
    value: unknown,
  ) => void;
  removeFilter: (id: string) => void;
  setFilters: (entries: SetFiltersEntry[]) => void;
  resetFilters: () => void;
  searchField: string;
};

export const FILTER_CONTEXT = createContext<FilterContextValue>({
  filters: [],
  query: '',
  setQuery: () => {},
  activeFilters: [],
  addFilter: () => {},
  removeFilter: () => {},
  setFilters: () => {},
  resetFilters: () => {},
  searchField: '',
});

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  (Object.getPrototypeOf(value) === Object.prototype ||
    Object.getPrototypeOf(value) === null);

const serializeFilterValue = (value: unknown): string => {
  if (isPlainObject(value)) {
    return JSON.stringify(value);
  }
  return String(value);
};

const deserializeFilterParam = (param: string): unknown => {
  try {
    const parsed = JSON.parse(param);
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed;
    }
  } catch {
    // not valid JSON, fall through
  }
  return param.split(',');
};

const getSearchParamsWithoutFilterParams = (
  searchParams: Record<string, string>,
  filterIds: string[],
): Record<string, string> => {
  const filterParamIds = new Set(['query', ...filterIds]);

  return Object.fromEntries(
    Object.entries(searchParams).filter(([key]) => !filterParamIds.has(key)),
  );
};

export function FiltersProvider({
  filters = [],
  children,
  storageKey,
  searchField = 'displayName',
  defaultFilters = [],
  disableQueryParams = false,
}: {
  filters?: FilterDefinition[];
  storageKey: string;
  children: ReactNode;
  searchField?: string;
  defaultFilters?: Array<{ id: string; value: unknown }>;
  disableQueryParams?: boolean;
}) {
  const [stateStorage, setStateStorage] = useLocalStorage<StoredFilter[]>(
    `filter-list-${storageKey}`,
    defaultFilters,
  );
  const { searchParams, setSearchParams } = useSearchParamsWithValues();
  const effectiveSearchParams = useMemo(
    () => (disableQueryParams ? {} : searchParams),
    [disableQueryParams, searchParams],
  );
  const effectiveSetSearchParams = useMemo(
    () => (disableQueryParams ? () => {} : setSearchParams),
    [disableQueryParams, setSearchParams],
  );
  const [activeFilters, setActiveFilters] = useState<ActiveFilterEntry[]>(
    () => {
      const storedFilters = Object.fromEntries(
        stateStorage.map(f => [f.id, f.value]),
      );
      const queryFilters = filters
        .filter(f => effectiveSearchParams[f.id] !== undefined)
        .map(f =>
          f.activate(
            typeof effectiveSearchParams[f.id] === 'string'
              ? deserializeFilterParam(effectiveSearchParams[f.id] as string)
              : effectiveSearchParams[f.id],
          ),
        );

      const filtersFromStorage = filters
        .filter(f => storedFilters[f.id] !== undefined)
        .map(f => f.activate(storedFilters[f.id]));

      return !queryFilters.length ? filtersFromStorage : queryFilters;
    },
  );

  const knownFilterIdsRef = useRef<Set<string>>(
    new Set(filters.map(f => f.id)),
  );

  useEffect(() => {
    const currentIds = new Set(filters.map(f => f.id));
    const newFilters = filters.filter(
      f => !knownFilterIdsRef.current.has(f.id),
    );

    if (newFilters.length > 0) {
      const storedFilters = Object.fromEntries(
        stateStorage.map(f => [f.id, f.value]),
      );

      const filtersFromUrl = newFilters
        .filter(f => effectiveSearchParams[f.id] !== undefined)
        .map(f =>
          f.activate(
            typeof effectiveSearchParams[f.id] === 'string'
              ? deserializeFilterParam(effectiveSearchParams[f.id] as string)
              : effectiveSearchParams[f.id],
          ),
        );

      const filtersFromStorage = newFilters
        .filter(
          f =>
            storedFilters[f.id] !== undefined &&
            effectiveSearchParams[f.id] === undefined,
        )
        .map(f => f.activate(storedFilters[f.id]));

      const filtersToRestore = [...filtersFromUrl, ...filtersFromStorage];

      if (filtersToRestore.length > 0) {
        setActiveFilters(current => {
          const existingIds = new Set(current.map(f => f.id));
          const toAdd = filtersToRestore.filter(f => !existingIds.has(f.id));
          return [...current, ...toAdd];
        });
      }
    }

    knownFilterIdsRef.current = currentIds;
  }, [filters, effectiveSearchParams, stateStorage]);

  const [query, setQuery] = useState(effectiveSearchParams.query ?? '');

  useDeepCompareEffect(() => {
    if (Object.keys(effectiveSearchParams).length === 0) {
      setStateStorage(activeFilters.map(f => ({ id: f.id, value: f.value })));
    }

    const params = activeFilters.reduce<Record<string, string>>(
      (acc, f) => {
        return { ...acc, [f.id]: serializeFilterValue(f.value) };
      },
      getSearchParamsWithoutFilterParams(
        effectiveSearchParams,
        filters.map(f => f.id),
      ),
    );

    params.query = query;

    effectiveSetSearchParams(params);
  }, [activeFilters, effectiveSearchParams, filters, query]);

  const commitFilters = useCallback(
    (updatedFilters: ActiveFilterEntry[]) => {
      setActiveFilters(updatedFilters);
      setStateStorage(updatedFilters.map(f => ({ id: f.id, value: f.value })));
    },
    [setStateStorage],
  );

  const addFilter = useCallback(
    (filter: FilterDefinition | ActiveFilterEntry, value: unknown) => {
      const existingFilter = activeFilters.find(f => f.id === filter.id);
      const updatedFilters = existingFilter
        ? activeFilters.map(f =>
            f.id === filter.id ? filter.activate(value) : f,
          )
        : [...activeFilters, filter.activate(value)];

      commitFilters(updatedFilters);
    },
    [activeFilters, commitFilters],
  );

  const removeFilter = useCallback(
    (id: string) => {
      commitFilters(activeFilters.filter(f => f.id !== id));
    },
    [activeFilters, commitFilters],
  );

  const setFilters = useCallback(
    (entries: SetFiltersEntry[]) => {
      commitFilters(entries.map(({ filter, value }) => filter.activate(value)));
    },
    [commitFilters],
  );

  const resetFilters = useCallback(() => {
    setActiveFilters([]);
    setQuery('');
    setStateStorage([]);
    effectiveSetSearchParams(
      getSearchParamsWithoutFilterParams(
        effectiveSearchParams,
        filters.map(f => f.id),
      ),
    );
  }, [
    setQuery,
    setStateStorage,
    effectiveSetSearchParams,
    effectiveSearchParams,
    filters,
  ]);

  const value = useMemo(
    () => ({
      filters,
      activeFilters,
      addFilter,
      removeFilter,
      setFilters,
      query,
      setQuery,
      searchField,
      resetFilters,
    }),
    [
      filters,
      activeFilters,
      addFilter,
      removeFilter,
      setFilters,
      query,
      setQuery,
      searchField,
      resetFilters,
    ],
  );

  return (
    <FILTER_CONTEXT.Provider value={value}>{children}</FILTER_CONTEXT.Provider>
  );
}

export function useFilters() {
  const context = useContext(FILTER_CONTEXT);

  if (!context)
    throw new Error('useFilterList must be used within a FilterListProvider');

  const {
    searchField,
    filters,
    activeFilters,
    query,
    resetFilters,
    setQuery,
    addFilter,
    removeFilter,
    setFilters,
  } = context;

  const filter = useMemo(() => {
    const parts = activeFilters
      .map(f => f.buildQuery())
      .filter(Boolean)
      .map(f => `(${f})`);

    if (query !== '') {
      const escaped = SearchUtils.escapeQuery(query);
      parts.push(`(${searchField}:${escaped}*)`);
    }

    return parts.join(' AND ');
  }, [activeFilters, query, searchField]);

  const isDefaultFilterState = useMemo(() => {
    return activeFilters.length === 0 && !query;
  }, [activeFilters, query]);

  return useMemo(
    () => ({
      searchQuery: query,
      filterString: filter,
      filters,
      activeFilters,
      resetFilters,
      isDefaultFilterState,

      setQuery,
      addFilter,
      removeFilter,
      setFilters,
    }),
    [
      query,
      filter,
      filters,
      activeFilters,
      resetFilters,
      isDefaultFilterState,
      setQuery,
      addFilter,
      removeFilter,
      setFilters,
    ],
  );
}
